import { supabase } from './supabase';
import { uploadAudioFile } from './ai';

const WEBHOOK_BASE = (import.meta.env.VITE_API_BASE as string) || '';
const WEBHOOK_TENANT_ID = (import.meta.env.VITE_WEBHOOK_TENANT_ID as string) || '2bmade';

// Geen client-side HMAC meer: het webhook-secret hoort niet in een publieke
// Vite-bundle (iedereen kon er geldige handtekeningen mee maken). De Jarvis
// endpoint valideert in plaats daarvan het Supabase access token van de
// ingelogde gebruiker.
async function callJarvis(path: string, payload: Record<string, unknown>): Promise<unknown> {
  if (!WEBHOOK_BASE) throw new Error('VITE_API_BASE niet geconfigureerd');
  const body = {
    action: path,
    data: payload,
    tenant_id: WEBHOOK_TENANT_ID,
  };
  const bodyStr = JSON.stringify(body);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Geen actieve sessie. Log opnieuw in en probeer het nog eens.');
  }
  headers.Authorization = `Bearer ${session.access_token}`;
  const res = await fetch(`${WEBHOOK_BASE}/${path}`, {
    method: 'POST',
    headers,
    body: bodyStr,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Jarvis webhook ${path} faalde: ${res.status} ${text.slice(0, 200)}`);
  }
  const text = await res.text();
  if (!text) return null;
  return JSON.parse(text);
}

export interface TranscribeAndWaitInput {
  file: File;
  clientId: string;
  onUploading?: () => void;
  onTranscribing?: () => void;
}

/**
 * Upload audio, trigger Jarvis Whisper-pipeline, poll client_notes (zelfde patroon
 * als hoofd-2BMade TranscriptionContext) en return de transcript-text.
 */
export async function transcribeAudioAndWait(input: TranscribeAndWaitInput): Promise<string> {
  const { file, clientId, onUploading, onTranscribing } = input;
  onUploading?.();

  // 1. Upload
  const { url } = await uploadAudioFile(file);

  // 2. Snapshot huidige transcription notes voor deze klant
  const { count: beforeCount } = await supabase
    .from('client_notes')
    .select('id', { count: 'exact', head: true })
    .eq('client_id', clientId)
    .eq('type', 'transcription');

  // 3. Trigger Jarvis pipeline
  await callJarvis('transcription/summarize', {
    client_id: clientId,
    file_url: url,
    file_name: file.name,
  });

  onTranscribing?.();

  // 4. Polling tot er een nieuwe rij verschijnt of timeout
  const startTime = Date.now();
  const POLL_INTERVAL = 3000;
  const TIMEOUT = 180_000;

  while (Date.now() - startTime < TIMEOUT) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL));

    const { data: notes, count: currentCount } = await supabase
      .from('client_notes')
      .select('id, content, created_at', { count: 'exact' })
      .eq('client_id', clientId)
      .eq('type', 'transcription')
      .order('created_at', { ascending: false })
      .limit(1);

    if (
      currentCount !== null &&
      beforeCount !== null &&
      currentCount > beforeCount &&
      notes &&
      notes.length > 0
    ) {
      return (notes[0].content as string) ?? '';
    }
  }

  throw new Error(
    'Transcriptie duurt langer dan 3 minuten. Check klantdossier in hoofd-2BMade voor de tekst.'
  );
}
